import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useToast, humanizeError } from '../shared/Toast'
import { BASELINE, BASELINE_ORDER } from './baseline'

export default function BaselineStep({
  recipientId,
  name,
  onComplete,
  stepLabel = 'Step 2 of 2',
  introTitle = 'One more thing',
  introText,
  finishLabel = 'Go to dashboard →',
}) {
  const toast = useToast()
  const [step, setStep] = useState(0) // 0–3 = cards, 4 = summary
  const [answers, setAnswers] = useState({}) // domainKey -> 1..4
  const [saving, setSaving] = useState(false)

  const domainKey = BASELINE_ORDER[step]
  const domain = BASELINE[domainKey]

  function choose(value) {
    setAnswers((a) => ({ ...a, [domainKey]: value }))
    // brief highlight, then advance
    setTimeout(() => setStep((s) => s + 1), 240)
  }

  async function finish() {
    if (saving) return
    setSaving(true)
    try {
      const { error } = await supabase
        .from('care_recipients')
        .update({
          baseline_mobility: answers.mobility ?? null,
          baseline_memory: answers.memory ?? null,
          baseline_mood: answers.mood ?? null,
          baseline_independence: answers.independence ?? null,
        })
        .eq('id', recipientId)
      if (error) throw error
      await onComplete()
    } catch (err) {
      toast.error(humanizeError(err))
      setSaving(false)
    }
  }

  const onSummary = step >= BASELINE_ORDER.length

  return (
    <div className="screen">
      <div className="container page" style={{ paddingTop: 20 }}>
        {/* Progress: optional step label + 4 question dots */}
        <div className="spread" style={{ marginBottom: 20 }}>
          <span className="section-title">{stepLabel || ' '}</span>
          <div className="row" style={{ gap: 6 }}>
            {BASELINE_ORDER.map((_, i) => (
              <div
                key={i}
                style={{
                  width: 22, height: 5, borderRadius: 3,
                  background: onSummary || i < step ? 'var(--teal)' : i === step ? 'var(--teal)' : 'var(--border)',
                  opacity: onSummary || i <= step ? 1 : 0.5,
                }}
              />
            ))}
          </div>
        </div>

        {!onSummary ? (
          <div className="stack">
            {step === 0 && (
              <div style={{ marginBottom: 4 }}>
                <h1 className="title" style={{ fontSize: 24 }}>{introTitle}</h1>
                <p className="subtitle mt-4">
                  {introText || `A quick snapshot of how ${name} is doing right now. 4 questions, about 90 seconds.`}
                </p>
              </div>
            )}

            <div className="row" style={{ gap: 8 }}>
              {step > 0 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, padding: 0 }}
                >
                  ← Back
                </button>
              )}
              <span className="muted" style={{ fontSize: 13 }}>Question {step + 1} of {BASELINE_ORDER.length}</span>
            </div>

            <h2 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
              {domain.question(name)}
            </h2>

            <div className="stack" style={{ gap: 10 }}>
              {domain.options.map((opt, i) => {
                const value = i + 1
                const on = answers[domainKey] === value
                return (
                  <button
                    key={i}
                    onClick={() => choose(value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, width: '100%',
                      minHeight: 64, padding: '12px 16px', textAlign: 'left',
                      borderRadius: 'var(--radius)',
                      border: on ? '2px solid var(--teal)' : '1.5px solid var(--border)',
                      background: on ? 'var(--teal)' : 'var(--surface)',
                      color: on ? '#fff' : 'var(--text)',
                      boxShadow: on ? 'none' : 'var(--shadow)',
                      transition: 'background 0.12s ease, border-color 0.12s ease',
                    }}
                  >
                    <span style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }} aria-hidden>{opt.icon}</span>
                    <span>
                      <span style={{ fontWeight: 700, fontSize: 16 }}>{opt.label}</span>
                      {opt.sub && (
                        <span style={{ display: 'block', fontSize: 13, marginTop: 2, color: on ? 'rgba(255,255,255,0.85)' : 'var(--muted)' }}>
                          {opt.sub}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        ) : (
          // Summary
          <div className="stack" style={{ paddingTop: 20 }}>
            <div className="center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 52 }}>🌱</div>
              <h1 className="title" style={{ fontSize: 26 }}>Baseline set</h1>
              <p className="subtitle center" style={{ maxWidth: 320 }}>
                Thanks — we've set {name}'s baseline. We'll track how each of these changes over time.
              </p>
            </div>

            <div className="card stack" style={{ gap: 12, marginTop: 8 }}>
              {BASELINE_ORDER.map((k) => {
                const v = answers[k]
                const opt = v ? BASELINE[k].options[v - 1] : null
                return (
                  <div key={k} className="spread">
                    <span className="label">{BASELINE[k].label}</span>
                    <span style={{ fontWeight: 600, textAlign: 'right' }}>
                      {opt ? `${opt.icon} ${opt.label}` : '—'}
                    </span>
                  </div>
                )
              })}
            </div>

            <button className="btn btn-lg btn-block mt-8" onClick={finish} disabled={saving}>
              {saving ? <span className="spinner" /> : finishLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
